/**
 * Card Component
 * Reusable card component for consistent layout
 */

import React from 'react';

interface CardProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  image?: {
    src: string;
    alt?: string;
    aspectRatio?: 'square' | 'video' | 'auto';
  };
  hoverable?: boolean;
  bordered?: boolean;
  className?: string;
  onClick?: () => void;
}

export const Card: React.FC<CardProps> = ({
  children,
  title,
  subtitle,
  image,
  hoverable = false,
  bordered = true,
  className = '',
  onClick,
}) => {
  const baseClasses = 'card bg-base-100 transition-all duration-200';
  
  const classes = [
    baseClasses,
    bordered ? 'card-bordered' : '',
    hoverable ? 'hover:shadow-lg cursor-pointer' : '',
    className,
  ].filter(Boolean).join(' ');

  const content = (
    <>
      {image && (
        <figure className={`${image.aspectRatio === 'square' ? 'aspect-square' : image.aspectRatio === 'video' ? 'aspect-video' : ''}`}>
          <img 
            src={image.src} 
            alt={image.alt || title || 'Card image'} 
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </figure>
      )}
      
      <div className="card-body">
        {title && (
          <h2 className="card-title">
            {title}
            {subtitle && <div className="badge badge-secondary">{subtitle}</div>}
          </h2>
        )}
        
        {children}
      </div>
    </>
  );

  if (onClick) {
    return (
      <div className={classes} onClick={onClick} role="button" tabIndex={0}>
        {content}
      </div>
    );
  }

  return (
    <div className={classes}>
      {content}
    </div>
  );
};

export default Card;
